import { Bell } from "lucide-react"

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import {
  SidebarMenu,
  SidebarMenuBadge,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from "@/components/ui/sidebar"
import { useStaffNotifications } from "@/components/layouts/staff-realtime"
import { EntryRequestActions } from "@/features/entry-requests"
import { cn } from "@/lib/utils"
import { t } from "@/lib/i18n"

/**
 * Above this the exact number stops changing any decision a person makes: eleven
 * pending requests and a hundred and eleven both mean "sit down and work through
 * the list".
 */
const MAX_SHOWN = 99

export function unreadLabel(count: number): string {
  return count > MAX_SHOWN ? `+${MAX_SHOWN}` : String(count)
}

/**
 * Was a navbar icon carrying a bare red dot. In the sidebar the row has room for
 * the count, so expanded it shows one; collapsed there is no room for a legible
 * number beside a 16px glyph, and `SidebarMenuBadge` hides itself on the icon
 * rail — so the dot comes back there, saying the one thing that fits: something
 * is waiting.
 */
export function StaffNotifications() {
  const { isMobile, state } = useSidebar()
  const collapsed = state === "collapsed" && !isMobile

  const { notifications, connectionMode } = useStaffNotifications()

  const count = notifications.length
  const label = t("ui.navigation.notifications")

  return (
    <SidebarMenu>
      <SidebarMenuItem>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <SidebarMenuButton
              data-testid="staff-notifications-trigger"
              aria-label={
                count > 0
                  ? t("ui.navigation.notifications_unread", { count })
                  : label
              }
              tooltip={collapsed ? label : undefined}
            >
              <Bell />
              <span>{label}</span>
            </SidebarMenuButton>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            side={isMobile ? "top" : "right"}
            align="end"
            sideOffset={8}
            className="w-80"
          >
            <div className="px-4 py-3">
              <p className="text-sm font-semibold">{label}</p>
              <p
                className="text-xs text-muted-foreground"
                data-testid="staff-notification-connection"
              >
                {connectionMode === "realtime"
                  ? t("ui.navigation.realtime")
                  : t("ui.navigation.resilient_mode")}
              </p>
            </div>
            <DropdownMenuSeparator />
            {count === 0 ? (
              <div className="p-6 text-center text-sm text-muted-foreground">
                {t("ui.navigation.no_new_notifications")}
              </div>
            ) : (
              <div className="max-h-80 overflow-y-auto p-2">
                {notifications.map((notification) => (
                  <article
                    key={notification.id}
                    data-testid="staff-notification-item"
                    className={cn(
                      "space-y-1 rounded-md border p-3 text-sm shadow-xs",
                      notification.critical
                        ? "border-warning/40 bg-warning/10"
                        : "border-border bg-card"
                    )}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <p className="font-medium">{notification.title}</p>
                      <time className="text-xs tabular-nums text-muted-foreground">
                        {new Date(notification.occurredAt).toLocaleTimeString("es-MX", {
                          hour: "2-digit",
                          minute: "2-digit",
                        })}
                      </time>
                    </div>
                    <p className="text-xs text-muted-foreground">{notification.message}</p>
                    {notification.kind === "entry_requested" && (
                      <EntryRequestActions
                        entryRequestId={notification.aggregateId}
                        approveHref={`/entry_requests/${notification.aggregateId}/approve`}
                        rejectHref={`/entry_requests/${notification.aggregateId}/reject`}
                      />
                    )}
                  </article>
                ))}
              </div>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
        {count > 0 && (
          <>
            <SidebarMenuBadge
              data-testid="staff-notifications-count"
              className="bg-destructive text-white"
            >
              {unreadLabel(count)}
            </SidebarMenuBadge>
            {/* The badge hides itself on the icon rail. A 16px glyph has no room
                beside a legible number, so the rail keeps the dot the navbar had. */}
            <span
              aria-hidden="true"
              data-testid="staff-notifications-dot"
              className="pointer-events-none absolute right-1 top-0.5 hidden size-2 rounded-full bg-destructive ring-2 ring-sidebar group-data-[collapsible=icon]:block"
            />
          </>
        )}
      </SidebarMenuItem>
    </SidebarMenu>
  )
}
